import { useRef, useState } from "react";
import { api, errMsg, errProblems, openBlob } from "../lib/api";
import { evalCond } from "../lib/cond";
import { Btn } from "./Shell";
import { StatusBadge } from "./StatusBadge";
import { toast } from "sonner";
import { FileDown, ShieldCheck, Pencil } from "lucide-react";

const show = (v) => (Array.isArray(v) ? (v.length && typeof v[0] === "object" ? `${v.length} entr${v.length > 1 ? "ies" : "y"}` : v.join(", ")) : v === true ? "Yes" : v || "-");

export function ReviewStep({ d, data, meta, onEdit }) {
  return (
    <div className="space-y-5" data-testid="review-step">
      {d.steps.map((s, i) => s.kind === "form" && (
        <div key={s.id} className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-slate-50 to-indigo-50/40 px-5 py-3">
            <span className="text-sm font-semibold">{s.title}</span>
            <div className="flex items-center gap-2">
              {d.progress.steps[s.id] ? <StatusBadge status="approved" prefix="Complete" /> : <StatusBadge status="correction_required" prefix="Incomplete" />}
              <Btn variant="ghost" data-testid={`review-edit-${s.id}`} onClick={() => onEdit(i)}><Pencil className="h-4 w-4" /> Edit</Btn>
            </div>
          </div>
          <dl className="grid gap-x-6 gap-y-2 p-4 text-sm sm:grid-cols-2">
            {s.fields.filter((f) => f.type !== "heading" && evalCond(f.show, data, meta)).map((f) => (
              <div key={f.key} className="flex justify-between gap-3 border-b border-dashed border-slate-100 py-1">
                <dt className="text-slate-500">{f.label}</dt><dd className="text-right font-medium">{show(data[f.key])}</dd>
              </div>
            ))}
          </dl>
          {s.fields.filter((f) => f.type === "repeat" && evalCond(f.show, data, meta)).map((f) => (data[f.key] || []).map((it, j) => (
            <div key={`${f.key}${j}`} className="mx-4 mb-3 rounded-lg bg-slate-50 p-3 text-xs">
              <b>{f.label} #{j + 1}</b>: {Object.entries(it).filter(([k]) => !k.endsWith("_enc")).map(([k, v]) => `${k}: ${show(v)}`).join(" · ")}
            </div>
          )))}
        </div>
      ))}
      <div className="rounded-xl border border-slate-200 p-4 text-sm">
        <b>Documents:</b> {d.checklist.complete ? "all required documents provided" : `missing ${d.checklist.missing_required.length} required item(s): ${d.checklist.missing_required.join(", ")}`}
      </div>
    </div>
  );
}

export function VerifyStep({ d, reload, editable }) {
  const v = d.application.verification || {};
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await api.post(`/applications/${d.application.id}/verify-account`); await reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const explain = {
    exact_match: "Name matches the bank's registered name.", partial_match: "Name partly matches. Operations will review manually - this is not an automatic rejection.",
    mismatch: "Name does not match. Operations will review; you may be asked for a bank letter.", unavailable: "Validation could not be completed now. Manual review will use your uploaded proof.",
    pending: "Validation is pending at the provider. Try again shortly.",
  };
  return (
    <div className="space-y-4" data-testid="verify-step">
      <p className="text-sm text-slate-600">We validate that the account exists and that the account-holder name matches your business. Account validation is <b>not</b> proof of KYC or of the account type.</p>
      {v.result && (
        <div className="rounded-xl border border-slate-200 p-4" data-testid="verification-result">
          <div className="flex items-center gap-2"><StatusBadge status={v.result} testid="verification-status" />{v.score != null && <span className="font-mono text-xs">similarity {v.score}</span>}{v.manual_decision && <StatusBadge status={v.manual_decision === "manual_approved" ? "approved" : "rejected"} prefix="Ops decision" />}</div>
          <p className="mt-2 text-sm">{explain[v.result]}</p>
          {v.note && <p className="mt-1 text-xs text-slate-500">{v.note}</p>}
        </div>
      )}
      {editable && <Btn data-testid="verify-account-btn" disabled={busy} onClick={run}><ShieldCheck className="h-4 w-4" /> {busy ? "Validating…" : v.result ? "Re-run validation" : "Validate account"}</Btn>}
    </div>
  );
}

const REQ = { bank_account: ["kyc_verification", "data_sharing", "terms"], account_verification: ["data_sharing", "terms"], product: ["data_sharing", "terms"] };

export function ConsentStep({ d, reload, editable, onDone }) {
  const app = d.application;
  const [forms, setForms] = useState(null);
  const [checked, setChecked] = useState({});
  const [problems, setProblems] = useState([]);
  const [busy, setBusy] = useState(false);
  const idem = useRef(crypto.randomUUID());
  const purposes = REQ[app.category];
  const ver = app.snapshot_version + 1;
  const given = new Set(d.consents.filter((c) => c.snapshot_version === ver).map((c) => c.purpose));
  if (forms === null) api.get(`/applications/${app.id}/forms`).then((r) => setForms(r.data)).catch(() => setForms([]));
  const exec = app.execution?.mode;
  const setExec = async (mode) => { try { await api.post(`/applications/${app.id}/execution`, { mode }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const consent = async () => {
    try {
      await api.post(`/applications/${app.id}/consents`, { purposes: purposes.filter((p) => checked[p] && !given.has(p)) });
      toast.success("Consent recorded");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const submit = async () => {
    setBusy(true);
    setProblems([]);
    try {
      await api.post(`/applications/${app.id}/submit`, {}, { headers: { "Idempotency-Key": idem.current } });
      onDone();
    } catch (e) { setProblems(errProblems(e)); toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const text = (p) => (d.consents.find((c) => c.purpose === p)?.text) || { kyc_verification: "I authorise verification of my identity and address through the selected bank-approved KYC route. OTPs, PINs and biometrics are never stored.", data_sharing: `I consent to sharing my application data and documents with ${d.provider?.name} for this application.`, terms: "I accept the product terms. Submitting is not an approval." }[p];
  return (
    <div className="space-y-6" data-testid="consent-step">
      {forms?.length > 0 && (
        <section>
          <h3 className="mb-2 font-semibold">Application forms</h3>
          <div className="space-y-2">{forms.map((f) => (
            <div key={f.code} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 p-3 text-sm">
              <span className="font-medium">{f.name}</span><span className="font-mono text-xs text-slate-500">v{f.version} · eff. {f.effective_date}</span>
              {f.approval_status !== "approved" && <span className="text-[11px] font-semibold uppercase text-amber-700">{f.approval_status.replace(/_/g, " ")}</span>}
              <div className="ml-auto flex gap-2">
                <Btn variant="outline" data-testid={`form-preview-${f.code}`} onClick={async () => { const r = await openBlob(`/applications/${app.id}/forms/${f.code}/preview`); const m = r.headers["x-missing-fields"]; if (m && m !== "0") toast.warning(`${m} mandatory field(s) still missing`); }}><FileDown className="h-4 w-4" /> Preview PDF</Btn>
                <Btn variant="ghost" data-testid={`form-finalise-${f.code}`} onClick={async () => { try { await api.post(`/applications/${app.id}/forms/${f.code}/finalise`); toast.success("Immutable copy stored"); reload(); } catch (e) { toast.error(errMsg(e)); setProblems(errProblems(e)); } }}>Finalise</Btn>
              </div>
            </div>))}
          </div>
          {d.forms.length > 0 && <p className="mt-2 text-xs text-slate-500">{d.forms.length} finalised immutable copy(ies) stored. A completed form is not an approval.</p>}
        </section>
      )}
      {app.category !== "account_verification" && (
        <section>
          <h3 className="mb-2 font-semibold">How will the form be executed?</h3>
          <div className="grid gap-2 sm:grid-cols-3">
            {[["digital", "Digital execution", "Electronic acceptance via provider eSign (sandbox only)"], ["upload_signed", "Download, sign & upload", "Upload the signed copy in Documents"], ["assisted_physical", "Assisted physical collection", "An agent collects the signed form"]].map(([m, l, s]) => (
              <button key={m} disabled={!editable} data-testid={`exec-${m}`} onClick={() => setExec(m)} className={`min-h-[44px] rounded-xl border p-4 text-left text-sm transition-all duration-200 ${exec === m ? "border-transparent sx-grad text-white shadow-lg shadow-indigo-500/25" : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-md"}`}>
                <div className="font-semibold">{l}</div><div className={`text-xs ${exec === m ? "text-indigo-100" : "text-slate-500"}`}>{s}</div>
              </button>))}
          </div>
          <p className="mt-2 text-xs text-slate-500">We never generate signatures or stamps.</p>
        </section>
      )}
      <section>
        <h3 className="mb-2 font-semibold">Consent & declarations</h3>
        {!d.viewer_is_owner && <p className="mb-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid="agent-consent-warning">The customer must sign in with their own account to give consent. Agents cannot consent on their behalf.</p>}
        <div className="space-y-2">{purposes.map((p) => (
          <label key={p} className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/50 p-4 text-sm transition-colors hover:border-indigo-200">
            <input type="checkbox" className="mt-1 h-5 w-5 accent-indigo-600" data-testid={`consent-${p}`} disabled={given.has(p) || !d.viewer_is_owner || !editable} checked={given.has(p) || !!checked[p]} onChange={(e) => setChecked({ ...checked, [p]: e.target.checked })} />
            <span><b className="capitalize">{p.replace(/_/g, " ")}</b> {given.has(p) && <span className="ml-1 text-xs text-emerald-700">(recorded)</span>}<br /><span className="text-slate-600">{text(p)}</span></span>
          </label>))}
        </div>
        {d.viewer_is_owner && editable && !app.consents_ok && <Btn className="mt-3" variant="outline" data-testid="give-consent-btn" disabled={!purposes.some((p) => checked[p] && !given.has(p))} onClick={consent}>Record my consent</Btn>}
      </section>
      {problems.length > 0 && (
        <div role="alert" className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="submit-problems">
          <b>Cannot submit yet:</b>
          <ul className="list-disc pl-5">{problems.map((p, i) => <li key={i}><b>{p.type}</b>: {typeof p.detail === "string" ? p.detail : Array.isArray(p.detail) ? p.detail.join(", ") : Object.keys(p.detail).join(", ")}</li>)}</ul>
        </div>
      )}
      {editable && d.viewer_is_owner && <Btn variant="accent" className="w-full sm:w-auto" data-testid="submit-application-btn" disabled={busy} onClick={submit}>{busy ? "Submitting…" : app.internal_status === "correction_required" ? "Resubmit corrected application" : "Submit application"}</Btn>}
    </div>
  );
}
